import type { User } from '@/lib/types/core';
import { normalizeUsername } from './username';

export type AccountStatus = 'pending' | 'active' | 'disabled' | 'conflict_review';

type ProviderLike = { providerId?: string | null };

type FirebaseUserLike = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  providerData?: ProviderLike[];
};

type ProfileOptions = {
  username?: string | null;
  createdBy: string;
};

type PendingProfileOptions = {
  uid: string;
  email: string | null;
  name: string;
  username: string | null;
  createdBy: string;
};

export const SELF_PROFILE_UPDATE_KEYS = [
  'name',
  'beem_id',
  'telegram_username',
  'custom_photo_url',
  'photo_url',
  'default_buyin_method',
  'default_payment_method',
  'hide_guests_on_leaderboard',
  'eft_account_name',
  'eft_bsb',
  'eft_account_number',
] as const;

export const RESTRICTED_CREDENTIAL_FIELDS = [
  'password',
  'temporary_password',
  'password_hash',
  'reset_link',
  'id_token',
  'oauth_token',
] as const;

function now() {
  return new Date().toISOString();
}

export function deriveProviderState(providerData: ProviderLike[]) {
  const providerIds = providerData
    .map((provider) => provider.providerId)
    .filter((providerId): providerId is string => Boolean(providerId));

  return {
    provider_ids: providerIds,
    password_login_enabled: providerIds.includes('password'),
    google_login_enabled: providerIds.includes('google.com'),
  };
}

function baseProfile(fields: {
  uid: string;
  email: string | null;
  name: string;
  photoURL?: string | null;
  username?: string | null;
  createdBy: string;
  accountStatus: AccountStatus;
  providerData?: ProviderLike[];
  passwordResetRequired?: boolean;
}): User {
  const username = fields.username ?? null;
  const providerState = deriveProviderState(fields.providerData ?? []);

  return {
    id: fields.uid,
    name: fields.name || 'Unknown',
    email: fields.email,
    photo_url: fields.photoURL ?? null,
    custom_photo_url: null,
    telegram_username: null,
    telegram_id: null,
    beem_id: null,
    is_regular: true,
    is_active: fields.accountStatus === 'active',
    entertainment_balance: 0,
    entertainment_unit_weight: 1,
    auto_approve_expenses: false,
    deactivated_at: null,
    entertainment_participant: true,
    entertainment_marked_at: null,
    entertainment_unmarked_at: null,
    entertainment_marked_by: null,
    entertainment_unmarked_by: null,
    hide_guests_on_leaderboard: false,
    pending_promotion: false,
    created_at: now(),
    updated_at: now(),
    last_active: now(),
    auth_created_at: now(),
    last_login_at: null,
    created_by: fields.createdBy,
    updated_by: fields.createdBy,
    default_buyin_method: 'digital',
    default_payment_method: null,
    username,
    username_normalized: username ? normalizeUsername(username) : null,
    account_status: fields.accountStatus,
    password_reset_required: fields.passwordResetRequired ?? false,
    roles: {
      app_admin: false,
      entertainment_admin: false,
      player: true,
    },
    ...providerState,
  };
}

export function createActiveUserProfileFromFirebase(
  firebaseUser: FirebaseUserLike,
  options: ProfileOptions,
): User {
  return baseProfile({
    uid: firebaseUser.uid,
    email: firebaseUser.email ?? null,
    name: firebaseUser.displayName ?? options.username ?? 'Unknown',
    photoURL: firebaseUser.photoURL,
    username: options.username ?? null,
    createdBy: options.createdBy,
    accountStatus: 'active',
    providerData: firebaseUser.providerData ?? [],
  });
}

export function createPendingUserProfile(options: PendingProfileOptions): User {
  return baseProfile({
    uid: options.uid,
    email: options.email,
    name: options.name,
    username: options.username,
    createdBy: options.createdBy,
    accountStatus: 'pending',
  });
}

export function createAdminTemporaryPasswordProfile(options: PendingProfileOptions): User {
  return baseProfile({
    uid: options.uid,
    email: options.email,
    name: options.name,
    username: options.username,
    createdBy: options.createdBy,
    accountStatus: 'active',
    providerData: [{ providerId: 'password' }],
    passwordResetRequired: true,
  });
}

export function deriveAccountRoute(
  profile: Pick<User, 'account_status' | 'password_reset_required'> | null,
): string | null {
  if (!profile) return '/account/pending';
  if (profile.password_reset_required) return '/account/security';
  if (profile.account_status === 'pending') return '/account/pending';
  if (profile.account_status === 'disabled') return '/account/disabled';
  if (profile.account_status === 'conflict_review') return '/account/conflict';
  return null;
}

export function getSelfProfileUpdateKeys(): string[] {
  return [...SELF_PROFILE_UPDATE_KEYS];
}

export function canAdminEditProfileField(field: string): boolean {
  void field;
  return true;
}

export function getRestrictedCredentialFields(): string[] {
  return [...RESTRICTED_CREDENTIAL_FIELDS];
}
